import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Tabs,
  Tab,
  Grid,
  Button,
  useTheme,
} from "@mui/material";
import { GET, PUT } from "../../Generic/CrudRequest";
import { useLocation } from "react-router-dom";
import PermisosWebList from "./PermissionWebList";
import PermisosMovilList from "./PermissionMovilList";
import Icons from "../../../Utils/Icons";
import { useTranslation } from "react-i18next";

export default function PermissionUser({ config }) {
  const { t, i18n } = useTranslation();
  const [value, setValue] = useState("1");
  const [permisosWeb, setPermisosWeb] = useState([]);
  const [permisosMovil, setPermisosMovil] = useState([]);
  const [modifiedPermisos, setModifiedPermisos] = useState([]);
  const [userId, setUserId] = useState("");
  const theme = useTheme();
  const location = useLocation();

  useEffect(() => {
    const fetchPermissions = async () => {
      const userIdPath = getUserIdFromPath(location.pathname);
      setUserId(userIdPath);
      const dataPermissions = await GET(
        `${config.url}/api/User/Permisos/${userIdPath}`
      );
      setPermisosWeb(dataPermissions.permisosWeb);
      setPermisosMovil(dataPermissions.permisosMovil);
    };

    fetchPermissions();
  }, [config.url, location.pathname, userId]);

  // Escuchar cambios de idioma y recargar la página
  useEffect(() => {
    const handleLanguageChange = () => {
      // Recargar la página cuando cambie el idioma
      window.location.reload();
    };

    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const getUserIdFromPath = (pathname) => {
    const parts = pathname.split("/");
    return parts[parts.length - 1];
  };

  const handlePermisosChange = (modifiedPermisos) => {
    setModifiedPermisos(modifiedPermisos);
  };

  const getPermisosWeb = (modifiedPermisos, userIdPermisos) => {
    const flattenPermisos = (permisos) => {
      return permisos.flatMap((permiso) => {
        const permisoWeb = {
          IdPermisoWebUsuario: 0,
          IdUsuario: userIdPermisos,
          IdPermisoWeb: permiso.idPermisoWeb,
          EsInicio: false,
          Estatus: permiso.estatus,
        };

        const permisosHijos = permiso.inverseIdPermisoPadreNavigation;
        if (permisosHijos && permisosHijos.length > 0) {
          const hijos = flattenPermisos(permisosHijos);
          return [permisoWeb, ...hijos];
        }

        return [permisoWeb];
      });
    };

    return flattenPermisos(modifiedPermisos);
  };

  const getPermisosMovil = (modifiedPermisos, userIdPermisos) => {
    const flattenPermisos = (permisosMov) => {
      return permisosMov.flatMap((permiso) => {
        const permisoMov = {
          IdPermisoMovilUsuario: 0,
          IdUsuario: userIdPermisos,
          IdPermisoMovil: permiso.idPermisoMovil,
          EsInicio: false,
          Estatus: permiso.estatus,
        };

        const permisosHijos = permisoMov.inverseIdPermisoMovilPadreNavigation;
        if (permisosHijos && permisosHijos.length > 0) {
          const hijos = flattenPermisos(permisosHijos);
          return [permisoMov, ...hijos];
        }

        return [permisoMov];
      });
    };

    return flattenPermisos(modifiedPermisos);
  };

  const handleSubmit = async () => {
    if (value === "1") {
      const permisosArray = getPermisosWeb(modifiedPermisos, userId);
      await PUT(
        `${config.url}/api/User/editar-permisos/${userId}`,
        permisosArray
      );
    } else if (value === "2") {
      const permisosMovilArray = getPermisosMovil(modifiedPermisos, userId);
      await PUT(
        `${config.url}/api/User/editar-permisos-moviles/${userId}`,
        permisosMovilArray
      );
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        overflow: "hidden",
        boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
        border: "1px solid rgba(0, 0, 0, 0.1)",
        borderRadius: 2,
      }}
    >
      <CardContent>
        <CardContent sx={{ display: "flex", alignItems: "center" }}>
          <Icons
            iconName="ArrowBack"
            fontSize="large"
            style={{
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              marginRight: 20,
            }}
            to="/Usuarios"
            tooltip={t("buttons.back")}
          />
          <Typography
            variant="h3"
            component="h2"
            sx={{ flex: "1", fontWeight: "bold" }}
          >
            {t("permissions.userPermissionsTitle")}
          </Typography>
        </CardContent>
        <Divider sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }} />
        <CardContent
          sx={{
            maxWidth: "calc(100% - 32px)",
            margin: "auto",
          }}
        >
          <Tabs
            value={value}
            onChange={handleChange}
            indicatorColor="primary"
            textColor="inherit"
            aria-label={t("permissions.userPermissionsTitle")}
            sx={{
              backgroundColor: theme.palette.primary.main,
              borderRadius: 2,
            }}
          >
            <Tab
              label={t("permissions.webPermissions")}
              value="1"
              sx={{ color: theme.palette.white.main }}
            />
            <Tab
              label={t("permissions.mobilePermissions")}
              value="2"
              sx={{ color: theme.palette.white.main }}
            />
          </Tabs>
        </CardContent>
        <CardContent
          sx={{
            maxWidth: "calc(100% - 32px)",
            margin: "auto",
          }}
        >
          {value === "1" && (
            <PermisosWebList
              permisos={permisosWeb}
              onPermisosChange={handlePermisosChange}
            />
          )}
          {value === "2" && (
            <PermisosMovilList
              permisosMovil={permisosMovil}
              onPermisosMovilChange={handlePermisosChange}
            />
          )}
        </CardContent>
        <CardContent
          sx={{
            maxWidth: "calc(100% - 32px)",
            margin: "auto",
          }}
        >
          <Grid container justifyContent="flex-end">
            <Button variant="contained" color="primary" onClick={handleSubmit}>
              {t("buttons.save")}
            </Button>
          </Grid>
        </CardContent>
      </CardContent>
    </Card>
  );
}
